<script setup>
import { has } from 'lodash'
import { computed } from 'vue'

import {
  BUTTON_ICON_DENSITIES,
  BUTTON_SIZES_NEXT,
  BUTTON_VARIANTS
} from '@/utils/components-configurations/app-button'
import { ASSIGNEE_ENTITY_KEYS } from '@/utils/entity-keys'
import { GLOBAL_GROUP_KEYS } from '@/utils/global-groups'

import GroupIcon from '@/components/global-groups/GroupIcon'
import {
  MEMBERSHIP_MARK_TEST_ID,
  SIDEBAR_ITEM_EXPAND_BUTTON_TEST_ID,
  SIDEBAR_ITEM_NAME_TEST_ID,
  SIDEBAR_ITEM_TEST_ID
} from '@/components/home-page/jest-helpers'
import AppAvatar from '@/components/ui/AppAvatar/AppAvatar'
import AppButtonNext from '@/components/ui/AppButtonNext/AppButtonNext'
import AppIcon from '@/components/ui/AppIcon/AppIcon'

defineOptions({
  name: 'HomepageSidebarFilterItem'
})

const emit = defineEmits({
  click: null,
  expand: null
})

const props = defineProps({
  item: {
    type: Object,
    required: true
  },

  itemLabel: {
    type: String,
    default: GLOBAL_GROUP_KEYS.NAME
  },

  selected: {
    type: Boolean
  },

  interactive: {
    type: Boolean,
    default: true
  },

  pinned: {
    type: Boolean
  },

  filteredOut: {
    type: Boolean
  },

  hideActions: {
    type: Boolean
  },

  expandable: {
    type: Boolean
  },

  // the tooltip and the aria-label of the membership mark: the user's own groups on the Groups tab
  // and the user on the Users tab, the mark is hidden when null
  memberTooltip: {
    type: String,
    default: null
  }
})

const buttonVariant = computed(() => {
  return props.selected ? BUTTON_VARIANTS.PRIMARY : BUTTON_VARIANTS.LINK_GRAY
})

const onClick = () => {
  if (props.interactive) {
    emit('click')
  }
}
</script>

<template>
  <div
    :class="{
      'hsfi-Item-selected': selected,
      'hsfi-Item-interactive': interactive,
      'hsfi-Item-pinned': pinned,
      'hsfi-Item-filtered-out': filteredOut
    }"
    :data-auto-testid="SIDEBAR_ITEM_TEST_ID"
    :data-testid="SIDEBAR_ITEM_TEST_ID"
    :role="interactive ? 'button' : null"
    :tabindex="interactive ? 0 : null"
    class="hsfi-Item"
    @click="onClick"
    @keydown.enter.self="onClick"
    @keydown.space.self.prevent="onClick"
  >
    <GroupIcon
      v-if="has(item, GLOBAL_GROUP_KEYS.ICON) && has(item, GLOBAL_GROUP_KEYS.COLOR)"
      :color="item[GLOBAL_GROUP_KEYS.COLOR]"
      :icon-name="item[GLOBAL_GROUP_KEYS.ICON]"
      class="hsfi-Item_GroupIcon"
    />

    <AppAvatar
      v-else
      :avatar-url="item[ASSIGNEE_ENTITY_KEYS.AVATAR_URL]"
      class="hsfi-Item_Avatar"
      no-margins
    />

    <span
      :data-auto-testid="SIDEBAR_ITEM_NAME_TEST_ID"
      :data-testid="SIDEBAR_ITEM_NAME_TEST_ID"
      class="oboard-truncated-text hsfi-Item_Name"
    >
      {{ item[itemLabel] }}
    </span>

    <span
      v-if="memberTooltip"
      v-tippy="{
        content: memberTooltip,
        placement: 'top'
      }"
      :aria-label="memberTooltip"
      :data-auto-testid="MEMBERSHIP_MARK_TEST_ID"
      :data-testid="MEMBERSHIP_MARK_TEST_ID"
      class="hsfi-Item_MembershipMark"
      role="img"
    >
      <AppIcon height="12" icon-name="membership-mark" width="12" />
    </span>

    <div v-if="!hideActions && (expandable || $slots.actions)" class="hsfi-Item_Actions">
      <slot :button-variant="buttonVariant" :selected="selected" name="actions">
        <AppButtonNext
          :data-auto-testid="SIDEBAR_ITEM_EXPAND_BUTTON_TEST_ID"
          :data-testid="SIDEBAR_ITEM_EXPAND_BUTTON_TEST_ID"
          :icon-density="BUTTON_ICON_DENSITIES.FIT"
          :size="BUTTON_SIZES_NEXT.XS"
          :variant="buttonVariant"
          icon-before="okr-table-expand"
          @click.stop="emit('expand')"
        />
      </slot>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.hsfi-Item {
  padding: 8px;
  border-radius: $border-radius-sm-next;
  display: flex;
  align-items: center;
  gap: 8px;
  color: $dark-1;
  font-weight: fw('semi-bold');
  font-size: $fs-14;
  line-height: 20px;
  transition: background-color $transition-fast ease-in-out;

  &-interactive {
    cursor: pointer;
    @media (any-hover: hover) {
      &:not(.hsfi-Item-selected):hover {
        background-color: $grey-3-next;
        color: $primary-color-next;
      }
    }

    &:focus-visible {
      &:not(.hsfi-Item-selected) {
        background-color: $grey-3-next;
        color: $primary-color-next;
      }
    }
  }

  &-selected {
    background-color: $primary-color-next;
    color: $white;
  }

  // eslint-disable-next-line vue-scoped-css/no-unused-selector
  &-pinned#{&}-selected {
    background-color: var(--primary-color-next-50);
    color: $dark-1;
  }

  &-filtered-out {
    opacity: 0.5;
  }
}

.hsfi-Item_Name {
  @media (any-hover: hover) {
    .hsfi-Item-interactive:not(.hsfi-Item-selected) &:hover {
      text-decoration: underline;
    }
  }
}

.hsfi-Item_Actions {
  margin-left: auto;
}

.hsfi-Item_MembershipMark {
  --ob-mm-color: var(--gray-400);
  --ob-mm-bg-color: var(--primary-color-next-50);
  display: flex;
  flex-shrink: 0;
  padding: 4px;

  .hsfi-Item-selected & {
    --ob-mm-color: #{$white};
    --ob-mm-bg-color: var(--primary-color-next-450);
  }

  .hsfi-Item-pinned.hsfi-Item-selected & {
    --ob-mm-color: #{$dark-2};
    --ob-mm-bg-color: var(--primary-color-next-150);
  }
}

.hsfi-Item_GroupIcon {
  .hsfi-Item-selected & {
    --ob-gi-color: #{$white};
    --ob-gi-bg-color: transparent;
  }

  .hsfi-Item-pinned.hsfi-Item-selected & {
    --ob-gi-color: #{$dark-1};
  }
}

.hsfi-Item_Avatar:deep(.aa-Avatar) {
  .hsfi-Item-selected & {
    box-shadow: 0 0 0 1px $white;
  }

  .hsfi-Item-pinned.hsfi-Item-selected & {
    box-shadow: none;
  }
}
</style>
